import { last } from 'lodash';
import { Bounds, Vector } from '@ardoq/graph';
import { interp } from './math';

export const rectContains = (rect: Bounds, pos: Vector): boolean =>
  pos[0] >= rect[0] &&
  pos[1] >= rect[1] &&
  pos[0] <= rect[2] &&
  pos[1] <= rect[3];

export const rectIntersects = (a: Bounds, b: Bounds): boolean =>
  a[0] <= b[2] && a[1] <= b[3] && a[2] >= b[0] && a[3] >= b[1];

export const rectWidth = (rect: Bounds): number => rect[2] - rect[0];

export const rectHeight = (rect: Bounds): number => rect[3] - rect[1];

export const rectSize = (rect: Bounds): Vector => [
  rectWidth(rect),
  rectHeight(rect),
];

export const rectCenter = (rect: Bounds): Vector => [
  (rect[0] + rect[2]) / 2,
  (rect[1] + rect[3]) / 2,
];

export const rectBloat = (rect: Bounds, amount: number): Bounds => [
  rect[0] - amount,
  rect[1] - amount,
  rect[2] + amount,
  rect[3] + amount,
];

export const rectEnclose = (...args: Vector[]): Bounds => {
  if (args.length === 0) {
    return rectEmpty();
  }

  const ret: Bounds = [...args[0], ...args[0]];

  for (let i = 1; i < args.length; ++i) {
    const pt = args[i];

    if (pt[0] < ret[0]) {
      ret[0] = pt[0];
    } else if (pt[0] > ret[2]) {
      ret[2] = pt[0];
    }

    if (pt[1] < ret[1]) {
      ret[1] = pt[1];
    } else if (pt[1] > ret[3]) {
      ret[3] = pt[1];
    }
  }

  return ret;
};

export const rectEncloseRects = (args: Iterable<Bounds>): Bounds => {
  const ret: Bounds = [+Infinity, +Infinity, -Infinity, -Infinity];

  for (const rc of args) {
    ret[0] = Math.min(ret[0], rc[0], rc[2]);
    ret[1] = Math.min(ret[1], rc[1], rc[3]);
    ret[2] = Math.max(ret[2], rc[0], rc[2]);
    ret[3] = Math.max(ret[3], rc[1], rc[3]);
  }

  return ret;
};

export type RectFitType = 'min' | 'max' | 'width' | 'height';

export const rectFit = (
  rc: Bounds,
  size: Vector,
  type: RectFitType = 'min'
): Bounds => {
  let scale = 1;

  switch (type) {
    default:
    case 'min':
      scale = Math.min(rectWidth(rc) / size[0], rectHeight(rc) / size[1]);
      break;

    case 'max':
      scale = Math.max(rectWidth(rc) / size[0], rectHeight(rc) / size[1]);
      break;

    case 'width':
      scale = rectWidth(rc) / size[0];
      break;

    case 'height':
      scale = rectHeight(rc) / size[1];
      break;
  }

  const width = size[0] * scale;
  const height = size[1] * scale;
  const left = (rc[0] + rc[2] - width) * 0.5;
  const top = (rc[1] + rc[3] - height) * 0.5;

  return [left, top, left + width, top + height];
};

export const rectEmpty = (): Bounds => [
  Number.NaN,
  Number.NaN,
  Number.NaN,
  Number.NaN,
];

export const rectInterp = (
  p: number,
  from: Bounds,
  to: Bounds,
  target: Bounds | null
): Bounds => {
  const ret = target ?? [0, 0, 0, 0];

  ret[0] = interp(p, from[0], to[0]);
  ret[1] = interp(p, from[1], to[1]);
  ret[2] = interp(p, from[2], to[2]);
  ret[3] = interp(p, from[3], to[3]);

  return ret;
};

export const vectorMag = (a: Vector): number => Math.hypot(...a);

export const vectorDot = (a: Vector, b: Vector): number =>
  a[0] * b[0] + a[1] * b[1];

export const vectorsInterp = (
  p: number,
  from: Vector[],
  to: Vector[],
  target: Vector[] | null
): Vector[] => {
  const retLength = Math.max(from.length, to.length);
  const ret = target ?? new Array<Vector>(retLength);

  if (!target) {
    for (let i = 0; i < retLength; ++i) {
      ret[i] = [NaN, NaN];
    }
  }

  while (ret.length < retLength) {
    ret.push([NaN, NaN]);
  }

  if (ret.length > retLength) {
    ret.length = retLength;
  }

  if (from.length === 0 && to.length > 1) {
    const mid = Math.floor(to.length / 2);
    const v0 = to[0];
    const v1 = to[to.length - 1];

    for (let i = 0; i < mid; ++i) {
      ret[i][0] = interp(p, v0[0], to[i][0]);
      ret[i][1] = interp(p, v0[1], to[i][1]);
    }

    for (let i = mid; i < to.length; ++i) {
      ret[i][0] = interp(p, v1[0], to[i][0]);
      ret[i][1] = interp(p, v1[1], to[i][1]);
    }

    return ret;
  }

  for (let i = 0; i < Math.min(from.length, to.length); ++i) {
    ret[i][0] = interp(p, from[i][0], to[i][0]);
    ret[i][1] = interp(p, from[i][1], to[i][1]);
  }

  for (let i = from.length; i < to.length; ++i) {
    ret[i][0] = interp(p, from[from.length - 1][0], to[i][0]);
    ret[i][1] = interp(p, from[from.length - 1][1], to[i][1]);
  }

  for (let i = to.length; i < from.length; ++i) {
    ret[i][0] = interp(p, from[i][0], to[to.length - 1][0]);
    ret[i][1] = interp(p, from[i][1], to[to.length - 1][1]);
  }

  return ret;
};

export const stripColinearPoints = <T>(
  path: Iterable<T>,
  getX: (pt: T) => number,
  getY: (pt: T) => number
): T[] => {
  const stripped: T[] = [];
  const buf: T[] = []; // last two output points
  let u: number[] = [0, 0]; // direction between last two output points

  for (const currentPoint of path) {
    if (buf.length === 0) {
      stripped.push(currentPoint);
      buf.push(currentPoint);
      continue;
    }

    const lastPoint = last(buf)!;
    const lastPointX = getX(lastPoint);
    const lastPointY = getY(lastPoint);
    const currentPointX = getX(currentPoint);
    const currentPointY = getY(currentPoint);

    const isSamePoint =
      currentPointX === lastPointX && currentPointY === lastPointY;

    if (isSamePoint) {
      continue;
    }

    if (buf.length === 1) {
      buf.push(currentPoint);
      u = [currentPointX - lastPointX, currentPointY - lastPointY];
      continue;
    }

    const v = [currentPointX - lastPointX, currentPointY - lastPointY]; // direction from the last output point to this point

    if (u[0] * v[1] - u[1] * v[0] !== 0) {
      /* this point is not colinear with the last output point */
      stripped.push(lastPoint);
      u = v;
    }

    buf[1] = currentPoint;
  }

  if (stripped.length > 0 && buf.length > 0) {
    /* fixup and flush */
    stripped.push(last(buf)!);
  }

  return stripped;
};
